import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { Plus, Shuffle, X } from 'lucide-react';
import {
  DropdownMenuCheckboxItem, DropdownMenuItem, DropdownMenuSeparator,
} from '@repo/ui/src/components/ui/dropdown-menu';
import { cn } from '@repo/ui/src/lib/utils';
import { PageHeader } from '../../components/PageHeader';
import { useCan } from '../../hooks/useCan';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { useClasses } from '../../hooks/useClasses';
import { ClassFormSheet } from './ClassFormSheet';
import { ClassListTab } from './ClassListTab';
import { TimetableGridTab } from './TimetableGridTab';

type ClassesTab = 'list' | 'grid';

// localStorage, in try/catch (spec §5 / brief): a private window, cleared
// site data, or a browser that blocks storage must not throw — the classMode
// default (below) is the fallback either way.
const TAB_STORAGE_KEY = 'lophoc.classes.tab';

// The remembered tab is keyed by the classMode it was chosen under: a
// choice made while the org was "Học riêng" must not outlive a switch to
// "Lớp học chung" (Johnny, 2026-09-14: otherwise the Cài đặt › Hiển thị
// row looks inert, because the stored tab always wins over the mode default).
function readStoredTab(mode: 'COHORT' | 'INDIVIDUAL'): ClassesTab | null {
  try {
    const raw = window.localStorage.getItem(TAB_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { tab?: unknown; mode?: unknown };
    if (v.mode !== mode) return null;
    return v.tab === 'list' || v.tab === 'grid' ? v.tab : null;
  } catch {
    return null;
  }
}

function writeStoredTab(tab: ClassesTab, mode: 'COHORT' | 'INDIVIDUAL') {
  try {
    window.localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify({ tab, mode }));
  } catch {
    // Private window / storage blocked — the URL param still carries the
    // choice for this session, just not across a fresh tab.
  }
}

/**
 * "Thời khóa biểu" -> "Lớp học" (spec §5, handoff decision 9): one PageHeader
 * shared by both tabs (S2 and S3 render byte-identical headers), tab state in
 * `?tab=list|grid`. Default follows `classMode` — COHORT opens Danh sách,
 * INDIVIDUAL opens Lịch cố định hàng tuần (decision: a piano teacher's grid
 * is the view they already know) — with the last tab a browser chose
 * remembered in localStorage, falling back to the mode default.
 *
 * Fix wave (review I2): a cold load's `classMode` guess (`useOrgSettings`
 * pending -> `useClassMode`'s own `?? 'INDIVIDUAL'` fallback) used to get
 * WRITTEN into `TAB_STORAGE_KEY` by the effect below, so a COHORT org's
 * first-ever visit latched onto the grid tab and every later visit kept
 * reading its own wrong guess back out of storage — permanently, per
 * browser. `writeStoredTab` is now called ONLY from `setTab` (an explicit
 * tab click); while `?tab=` is absent, no stored choice exists yet, AND
 * `classMode` is still pending, `tab` resolves to `undefined` and the page
 * renders a loading placeholder instead of picking (and thereby risking
 * persisting) a guess.
 */
export function ClassesPage() {
  const { t } = useTranslation('scheduling');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data: orgSettings, isLoading: orgSettingsLoading } = useOrgSettings();
  const classMode = orgSettings?.classMode ?? 'INDIVIDUAL';
  const canManageSlots = useCan('manage:slots');

  const defaultTab: ClassesTab = classMode === 'COHORT' ? 'list' : 'grid';
  const tabParam = params.get('tab');
  // Pending classMode with nothing else to resolve from -> `tab` is
  // undefined (render the skeleton below) rather than guessing; the stored
  // tab is only trusted once the mode it was stored under can be compared.
  const settingsPending = orgSettings === undefined && orgSettingsLoading;
  const storedTab = settingsPending ? null : readStoredTab(classMode);
  const tab: ClassesTab | undefined = tabParam === 'list' || tabParam === 'grid'
    ? tabParam
    : (storedTab ?? (settingsPending ? undefined : defaultTab));

  const setTab = (next: ClassesTab) => {
    writeStoredTab(next, classMode);
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  };

  const showEnded = params.get('ended') === '1';
  const showArchived = params.get('archived') === '1';
  const setBoolFilter = (key: string, value: boolean) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) p.set(key, '1'); else p.delete(key);
      return p;
    }, { replace: true });
  };

  // "Xếp lớp hàng loạt" (README decision, S2/S3 header): the toggle lives in
  // this shared header, but bulk move only OPERATES on the grid — picking it
  // from the list tab switches there first. TimetableGridTab reacts to the
  // `moveMode` prop (see its own comment) instead of owning the boolean
  // itself, since the button that flips it lives up here.
  const [moveMode, setMoveMode] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (tab !== 'grid') setMoveMode(false);
  }, [tab]);

  const [createOpen, setCreateOpen] = useState(false);

  // Tab badge count: the org's live (non-ended, non-archived) class count,
  // same base bucket ClassListTab starts from — sharing that query key means
  // this costs nothing extra (React Query dedupes it against ClassListTab's
  // own fetch of the same bucket).
  const { data: baseClasses } = useClasses({ status: 'ACTIVE' });

  return (
    <div className="mx-auto w-full max-w-6xl p-6 sm:p-8">
      <PageHeader
        size="lg"
        title={t('classes.title')}
        subtitle={t('classes.subtitle')}
        actions={[
          {
            key: 'new-class',
            label: t('classes.newClass'),
            icon: Plus,
            onClick: () => setCreateOpen(true),
            disabledReason: canManageSlots ? undefined : tCommon('rbac.adminOnly'),
          },
          {
            key: 'bulk-assign',
            label: moveMode ? t('moveMode.toggleOff') : t('moveMode.toggleOn'),
            icon: moveMode ? X : Shuffle,
            variant: moveMode ? 'default' : 'outline',
            onClick: () => { if (!moveMode) setTab('grid'); setMoveMode((m) => !m); },
            // README decision 4 / S2 frame 4: hidden outright for MEMBER, not
            // merely disabled — a control nobody can use should not show.
            hidden: !canManageSlots,
          },
        ]}
        overflow={
          <>
            <DropdownMenuCheckboxItem
              checked={showEnded}
              onCheckedChange={(v) => setBoolFilter('ended', v)}
              onSelect={(e) => e.preventDefault()}
            >
              {t('classes.pageMenu.showEnded')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={showArchived}
              onCheckedChange={(v) => setBoolFilter('archived', v)}
              onSelect={(e) => e.preventDefault()}
            >
              {t('classes.pageMenu.showArchived')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              {/* #class-mode: DisplaySection scrolls to the "Tổ chức lớp" row
                  on mount when this hash is present (THI-267 Task 16). */}
              <Link to="/settings/display#class-mode">{t('classes.pageMenu.classModeSettings')}</Link>
            </DropdownMenuItem>
          </>
        }
      />

      {(showEnded || showArchived) && tab === 'list' ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {showEnded ? (
            <button
              type="button"
              onClick={() => setBoolFilter('ended', false)}
              className="inline-flex h-[25.6px] items-center gap-1.5 rounded-full bg-surface-100 pl-[9.6px] pr-1.5 text-[12.5px] font-medium text-surface-600"
            >
              {t('classes.filters.showEndedChip')}
              <X className="h-[12.8px] w-[12.8px] text-surface-400" />
            </button>
          ) : null}
          {showArchived ? (
            <button
              type="button"
              onClick={() => setBoolFilter('archived', false)}
              className="inline-flex h-[25.6px] items-center gap-1.5 rounded-full bg-surface-100 pl-[9.6px] pr-1.5 text-[12.5px] font-medium text-surface-600"
            >
              {t('classes.filters.showArchivedChip')}
              <X className="h-[12.8px] w-[12.8px] text-surface-400" />
            </button>
          ) : null}
        </div>
      ) : null}

      {tab === undefined ? (
        // Fix wave (I2) — `classMode` hasn't resolved yet and nothing
        // (`?tab=`, a remembered choice) says which tab to open: wait
        // rather than guess, so nothing gets written to storage that the
        // teacher never actually chose.
        <p className="flex h-24 items-center justify-center text-sm text-muted-foreground">{t('classes.list.loading')}</p>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-1 border-b border-surface-200">
            <button
              type="button"
              onClick={() => setTab('list')}
              className={cn(
                'inline-flex items-center gap-1.5 px-[12.8px] py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
                tab === 'list' ? 'border-brand-500 font-semibold text-brand-600' : 'border-transparent text-surface-500 hover:text-surface-800',
              )}
            >
              {t('classes.tabs.list')}
              {baseClasses ? (
                <span className="tabular rounded-full bg-surface-100 px-1.5 text-xs font-semibold text-surface-500">
                  {baseClasses.length}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              onClick={() => setTab('grid')}
              className={cn(
                'inline-flex items-center px-[12.8px] py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
                tab === 'grid' ? 'border-brand-500 font-semibold text-brand-600' : 'border-transparent text-surface-500 hover:text-surface-800',
              )}
            >
              {t('classes.tabs.grid')}
            </button>
          </div>

          {tab === 'list' ? (
            <ClassListTab showEnded={showEnded} showArchived={showArchived} />
          ) : (
            <TimetableGridTab moveMode={moveMode} onExitMoveMode={() => setMoveMode(false)} />
          )}
        </>
      )}

      <ClassFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={(detail) => navigate(`/classes/${detail.id}`)}
      />
    </div>
  );
}
